import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { usePostMessage, useReceiveMessage } from './messageContext';
import { useAppointment } from './appointmentContext';
import { useLocation } from 'react-router-dom';

const NavigationSyncContext = createContext();

export const useNavigationSync = () => useContext(NavigationSyncContext);

export const NavigationProvider = ({ children }) => {
  const [activeClinicianTab, setActiveClinicianTab] = useState('welcomeScreen');
  const [activePatientTab, setActivePatientTab] = useState(null);
  const { appointment } = useAppointment();
  const { pathname } = useLocation();
  const userIsPatient = appointment?.currentUser && appointment?.currentUser?.role === 'patient';
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();

  const redirectToWelcomePage = useCallback(
    (msg) => {
      const roomName = localStorage.getItem('room_name');
      if (!roomName || pathname.includes(roomName) || pathname.includes('error')) return postMessage({ type: 'initialisationComplete' });
      if (msg) alert(msg);
      setActiveClinicianTab('welcomeScreen');
      window.location.replace(`/start/${roomName}`);
    },
    [pathname, postMessage],
  );

  useEffect(() => {
    //clincian
    if (userIsPatient) return;

    switch (activeClinicianTab) {
      case 'welcomeScreen':
        return redirectToWelcomePage();
      case 'calibration-selection':
      case 'test':
        return postMessage({ type: activeClinicianTab });
      case 'set-distance':
        return postMessage({ type: 'pupillaryDistance' });
      case 'results':
        return postMessage({ type: 'endOfTest' });
      default:
        break;
    }
  }, [userIsPatient, activeClinicianTab, postMessage, redirectToWelcomePage]);

  useEffect(() => {
    const localUserRole = userIsPatient ? 'patient' : 'clinician';
    const remoteUserRole = userIsPatient ? 'clinician' : 'patient';
    if (receivedMessage?.type === 'welcomeScreen' && receivedMessage?.senderRole !== localUserRole) {
      const msg = `Something has gone wrong with your connection to the ${remoteUserRole}. You are being returned to the beginning of the examination.`;
      redirectToWelcomePage(msg);
    }
  }, [receivedMessage, userIsPatient, redirectToWelcomePage]);

  useEffect(() => {
    //patient
    if (!patientRoutes.includes(receivedMessage?.type) || !userIsPatient) return;
    switch (receivedMessage?.type) {
      case 'calibration-selection':
      case 'test':
      case 'holding':
        return setActivePatientTab('holding');
      case 'pupillaryDistance':
        return setActivePatientTab('pupillaryDistance');
      case 'blindspot':
        return setActivePatientTab('blindspot');
      case 'endOfTest':
        return setActivePatientTab('endOfTest');
      default:
        break;
    }
  }, [userIsPatient, receivedMessage, redirectToWelcomePage]);

  return (
    <NavigationSyncContext.Provider value={{ activeClinicianTab, setActiveClinicianTab, activePatientTab, setActivePatientTab }}>
      {children}
    </NavigationSyncContext.Provider>
  );
};

const patientRoutes = ['welcome', 'holding', 'calibration-selection', 'pupillaryDistance', 'blindspot', 'test', 'endOfTest'];
